import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { PURPOSES, DIAL_MODES, STATUS_STYLES, labelFor } from "@/lib/campaignMeta";
import { Megaphone, Phone, ArrowRight } from "@phosphor-icons/react";

export default function AgentCampaigns() {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/campaigns/assigned")
            .then(({ data }) => setCampaigns(data))
            .catch(() => { /* ignore */ })
            .finally(() => setLoading(false));
    }, []);

    return (
        <AppShell>
            <div className="mx-auto max-w-5xl px-6 py-10" data-testid="agent-campaigns-page">
                <div className="flex items-center gap-3">
                    <Megaphone size={26} weight="fill" className="text-brand" />
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-ink">My Campaigns</h1>
                        <p className="text-sm text-stone2">Campaigns assigned to you. Click a campaign to start dialing.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="mt-10 text-stone2" data-testid="agent-campaigns-loading">Loading your campaigns…</div>
                ) : campaigns.length === 0 ? (
                    <div className="mt-10 rounded-2xl border border-dashed border-stone-300 p-12 text-center" data-testid="agent-campaigns-empty">
                        <Megaphone size={32} className="mx-auto text-stone-300" />
                        <p className="mt-3 font-semibold text-ink">No campaigns assigned to you yet</p>
                        <p className="mt-1 text-sm text-stone2">When an admin assigns you to an active campaign, it will show up here.</p>
                    </div>
                ) : (
                    <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="agent-campaigns-list">
                        {campaigns.map((c) => (
                            <button
                                key={c.id}
                                data-testid={`agent-campaign-${c.id}`}
                                onClick={() => navigate(`/campaigns/${c.id}/run`)}
                                className="group flex flex-col rounded-2xl border border-stone-200 bg-white p-5 text-left transition-all hover:border-ink hover:shadow-md"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <span className="font-heading text-lg font-bold text-ink">{c.name}</span>
                                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_STYLES[c.status] || "bg-stone-100 text-stone-600"}`}>{c.status}</span>
                                </div>
                                {c.description && <p className="mt-1 line-clamp-2 text-sm text-stone2">{c.description}</p>}
                                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone2">
                                    <span>{labelFor(PURPOSES, c.purpose)}</span>
                                    <span>·</span>
                                    <span>{labelFor(DIAL_MODES, c.dialing_mode)}</span>
                                    <span>·</span>
                                    <span data-testid={`agent-campaign-pending-${c.id}`}>{c.pending} to call</span>
                                </div>
                                <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-brand">
                                    <Phone size={16} weight="fill" />
                                    Start dialing
                                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </AppShell>
    );
}
